/**
 * RBAC Permissions Registry — Zero Trust Security Layer
 * Every route, action, and resource requires explicit permission grant.
 * Default: DENY ALL. Access must be explicitly granted.
 */

export type UserRole = 'admin' | 'sre_lead' | 'devops_engineer' | 'compliance_auditor' | 'viewer';

export type Permission =
  | 'view_logs' |'run_diagnosis' |'execute_remediations' |'manage_integrations' |'manage_servers' |'invite_members' |'manage_team' |'manage_api_keys' |'view_analytics' |'export_reports' |'view_audit_trail' |'manage_llm' |'view_mlops' |'manage_notifications' |'view_incidents' |'manage_incidents';

/** Explicit permission grants per role — Zero Trust: deny by default */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: [
    'view_logs', 'run_diagnosis', 'execute_remediations',
    'manage_integrations', 'manage_servers', 'invite_members',
    'manage_team', 'manage_api_keys', 'view_analytics', 'export_reports',
    'view_audit_trail', 'manage_llm', 'view_mlops', 'manage_notifications',
    'view_incidents', 'manage_incidents',
  ],
  sre_lead: [
    'view_logs', 'run_diagnosis', 'execute_remediations',
    'manage_integrations', 'manage_servers', 'invite_members',
    'manage_api_keys', 'view_analytics', 'export_reports',
    'view_audit_trail', 'manage_llm', 'view_mlops', 'manage_notifications',
    'view_incidents', 'manage_incidents',
  ],
  devops_engineer: [
    'view_logs', 'run_diagnosis', 'execute_remediations',
    'manage_servers', 'manage_api_keys', 'view_analytics',
    'view_audit_trail', 'view_mlops', 'manage_notifications',
    'view_incidents', 'manage_incidents',
  ],
  compliance_auditor: [
    'view_logs', 'view_analytics', 'export_reports',
    'view_audit_trail', 'view_incidents',
  ],
  viewer: [
    'view_logs', 'view_analytics', 'view_incidents',
  ],
};

/** Route-to-required-permission mapping — Zero Trust route guard */
export const ROUTE_PERMISSIONS: Record<string, Permission> = {
  '/integrations-hub': 'manage_integrations',
  '/live-log-stream': 'view_logs',
  '/ai-diagnosis-dashboard': 'run_diagnosis',
  '/agentic-resolution': 'execute_remediations',
  '/llm-control-panel': 'manage_llm',
  '/mlops-dashboard': 'view_mlops',
  '/server-management': 'manage_servers',
  '/incident-history': 'view_incidents',
  '/audit-trail': 'view_audit_trail',
  '/analytics': 'view_analytics',
  '/api-usage-dashboard': 'view_analytics',
  '/integration-health': 'view_analytics',
  '/api-keys': 'manage_api_keys',
  '/secrets-vault': 'manage_api_keys',
  '/team-members': 'manage_team',
  '/notification-preferences': 'manage_notifications',
  '/user-profile': 'view_logs', // accessible to all authenticated users
  '/settings': 'view_logs',     // accessible to all authenticated users
};

/** API route permission requirements — Zero Trust API guard */
export const API_ROUTE_PERMISSIONS: Record<string, Permission> = {
  '/api/diagnosis/enqueue': 'run_diagnosis',
  '/api/realtime/publish': 'execute_remediations',
  '/api/realtime/subscribe': 'view_logs',
  '/api/queue/status': 'run_diagnosis',
  '/api/connectors': 'manage_integrations',
  '/api/ai/chat-completion': 'run_diagnosis',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  sre_lead: 'SRE Lead',
  devops_engineer: 'DevOps Engineer',
  compliance_auditor: 'Compliance Auditor',
  viewer: 'Viewer',
};

export const ROLE_COLORS: Record<UserRole, string> = {
  admin: '#ef4444',
  sre_lead: '#3b82f6',
  devops_engineer: '#10b981',
  compliance_auditor: '#8b5cf6',
  viewer: '#64748b',
};

/** Zero Trust: explicit deny check — never assume access */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  const granted = ROLE_PERMISSIONS[role];
  if (!granted) return false; // deny unknown roles
  return granted.includes(permission);
}

export function hasRouteAccess(role: UserRole, route: string): boolean {
  const requiredPermission = ROUTE_PERMISSIONS[route];
  if (!requiredPermission) return true; // public route
  return hasPermission(role, requiredPermission);
}

/** Nav items visible to a given role */
export function getVisibleNavItems(role: UserRole): string[] {
  return Object.entries(ROUTE_PERMISSIONS)
    .filter(([, perm]) => hasPermission(role, perm))
    .map(([route]) => route);
}
